import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus, Minus, HelpCircle } from 'lucide-react';
import { Container, Section, Badge } from '../styles/GlobalStyles';

const FAQSection = styled(Section)`
  background: linear-gradient(135deg, var(--gray-50) 0%, var(--primary-50) 100%);
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: var(--space-16);
  
  .badge {
    margin-bottom: var(--space-4);
  }
  
  h2 {
    font-size: var(--text-5xl);
    color: var(--gray-900);
    margin-bottom: var(--space-6);
    font-weight: 800;
    
    @media (max-width: 768px) {
      font-size: var(--text-4xl);
    }
  }
  
  .subtitle {
    font-size: var(--text-xl);
    color: var(--gray-600);
    max-width: 600px;
    margin: 0 auto;
  }
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  background: var(--gray-0);
  border: 1px solid var(--gray-200);
  border-radius: var(--radius-xl);
  margin-bottom: var(--space-4);
  transition: all var(--transition-fast);
  
  &:hover {
    border-color: var(--primary-300);
    box-shadow: var(--shadow-md);
  }
`;

const FAQQuestion = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-6);
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  font-size: var(--text-lg);
  font-weight: 600;
  color: var(--gray-900);
  transition: all var(--transition-fast);
  
  &:hover {
    color: var(--primary-600);
  }
  
  .icon {
    color: var(--primary-600);
    transition: transform var(--transition-fast);
    transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const FAQAnswer = styled.div`
  padding: 0 var(--space-6) var(--space-6);
  color: var(--gray-700);
  line-height: 1.6;
  font-size: var(--text-base);
  
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  opacity: ${props => props.isOpen ? '1' : '0'};
  max-height: ${props => props.isOpen ? '200px' : '0'};
  overflow: hidden;
  transition: all var(--transition-normal);
`;

const faqs = [
  {
    question: "What types of software do you develop?",
    answer: "We specialize in custom web applications, database management systems, e-commerce platforms, restaurant management systems like WillovateResto.com, and various business automation tools. We work with modern technologies like React, Node.js, .NET Core, and cloud platforms."
  },
  {
    question: "How long does a typical project take?",
    answer: "Project timelines vary based on complexity and requirements. A simple website might take 2-4 weeks, while a comprehensive management system could take 8-16 weeks. We provide detailed timelines during our initial consultation and keep you updated throughout the development process."
  },
  {
    question: "What is your development process?",
    answer: "We follow a structured approach: Discovery (1-2 weeks), Design (3-4 weeks), Development (5-12 weeks), and Launch & Support (13+ weeks). We maintain constant communication and provide regular updates throughout each phase."
  },
  {
    question: "Do you provide ongoing support after launch?",
    answer: "Yes! We offer comprehensive post-launch support including bug fixes, security updates, performance monitoring, and feature enhancements. We believe in building long-term partnerships with our clients."
  },
  {
    question: "What technologies do you use?",
    answer: "We use modern, industry-standard technologies including React.js, Node.js, .NET Core, C#, Python, MySQL, PostgreSQL, MongoDB, AWS, and more. We choose the best technology stack for each project based on your specific requirements and scalability needs."
  },
  {
    question: "How do you ensure project quality?",
    answer: "We follow best practices including thorough testing, code reviews, security audits, and performance optimization. Each project goes through multiple quality checkpoints before delivery, and we provide detailed documentation."
  }
];

const FAQ = () => {
  const [openItems, setOpenItems] = useState(new Set([0])); // First item open by default

  const toggleItem = (index) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  return (
    <FAQSection>
      <Container>
        <SectionHeader>
          <Badge className="badge primary">
            <HelpCircle size={12} />
            FAQ
          </Badge>
          <h2>Frequently Asked Questions</h2>
          <p className="subtitle">
            Get answers to common questions about our software development 
            services and process.
          </p>
        </SectionHeader>

        <FAQContainer>
          {faqs.map((faq, index) => (
            <FAQItem key={index}>
              <FAQQuestion
                isOpen={openItems.has(index)}
                onClick={() => toggleItem(index)}
              >
                <span>{faq.question}</span>
                <div className="icon">
                  {openItems.has(index) ? <Minus size={20} /> : <Plus size={20} />}
                </div>
              </FAQQuestion>
              <FAQAnswer isOpen={openItems.has(index)}>
                {faq.answer}
              </FAQAnswer>
            </FAQItem>
          ))}
        </FAQContainer>
      </Container>
    </FAQSection>
  );
};

export default FAQ;
